import { useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';
import { DOOH_REPORT_LOGO_EXTENSIONS } from 'sonata-static-storage/lib/entities/doohReport/doohReport.constants';

import ImageEditMenu from '@/components/common/ImageEditMenu/ImageEditMenu';
import { useDoohReportSettings } from '@/components/doohReport/common/hooks/useDoohReportSettings';
import { resetDoohReportLogo, uploadDoohReportLogo } from '@/services/doohReport.service';

import './reportLogo.scss';

type Props = {
  lineItemId: string;
};

/**
 * Logo of the DOOH report summary card: the one uploaded from the report or, failing that, the
 * advertiser's, with the inventory placeholder when there is none (or it fails to load). Outside
 * shared report sessions it can be changed or reset from the edit menu.
 */
export default function ReportLogo({ lineItemId }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { settings, updateSettings } = useDoohReportSettings(lineItemId);
  const [isSaving, setIsSaving] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const logo = settings?.logo ?? null;
  const showLogo = !!logo && logo.url !== failedUrl;

  const onUpload = async (rawFile: File) => {
    setIsSaving(true);
    try {
      const result = await uploadDoohReportLogo(lineItemId, rawFile);
      updateSettings({ logo: result.logo });
      showNotification(
        'success',
        formatMessage({ defaultMessage: 'Logo uploaded successfully', id: 'doohReport.summaryCard.logo.uploadSuccess' })
      );
    } catch (_error) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while uploading the logo. Please try again.',
          id: 'doohReport.summaryCard.logo.uploadError'
        })
      );
    }
    setIsSaving(false);
  };

  const onResetClick = async () => {
    setIsSaving(true);
    try {
      const result = await resetDoohReportLogo(lineItemId);
      updateSettings({ logo: result.logo });
      showNotification(
        'success',
        formatMessage({ defaultMessage: 'Logo reset successfully', id: 'doohReport.summaryCard.logo.resetSuccess' })
      );
    } catch (_error) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while resetting the logo. Please try again.',
          id: 'doohReport.summaryCard.logo.resetError'
        })
      );
    }
    setIsSaving(false);
  };

  return (
    <div className="reportLogo">
      {showLogo ? (
        // biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is a native img fallback handler
        <img
          alt={formatMessage({ defaultMessage: 'Report logo', id: 'doohReport.summaryCard.logo.alt' })}
          className="reportLogo__image"
          onError={() => setFailedUrl(logo.url)}
          src={logo.url}
        />
      ) : (
        <i
          className="fa-kit-duotone fa-poi-inventory reportLogo__placeholderIcon"
          data-testid="reportLogoPlaceholder"
        />
      )}
      <ImageEditMenu
        accept={DOOH_REPORT_LOGO_EXTENSIONS.join(',')}
        buttonClassName="reportLogo__editButton"
        buttonShape="circle"
        buttonSize="ultraSmall"
        inputName="dooh-report-logo-upload"
        isLoading={isSaving}
        onReset={logo?.source === 'user' ? onResetClick : undefined}
        onUpload={onUpload}
        texts={{
          change: formatMessage({ defaultMessage: 'Change logo', id: 'doohReport.summaryCard.logo.change' }),
          edit: formatMessage({ defaultMessage: 'Edit logo', id: 'doohReport.summaryCard.logo.edit' }),
          fileTooLarge: formatMessage({
            defaultMessage: 'The image exceeds the maximum size of 2MB.',
            id: 'doohReport.summaryCard.logo.fileTooLarge'
          }),
          reset: formatMessage({ defaultMessage: 'Reset to default', id: 'doohReport.summaryCard.logo.reset' })
        }}
        tooltipPlacement="right-start"
      />
    </div>
  );
}
